/**
 * Farm settings vocabulary: market presets, the validators both the API and the
 * setup screen use, and the farm's own calendar day.
 *
 * Pure, like the rest of this package (CLAUDE.md §5). Everything here leans on
 * the runtime's built-in `Intl` data, which Workers, Node and every supported
 * browser all ship.
 */
import type { IsoDate, NumericString } from './index'

/**
 * Starting values for a new farm, by market. The setup screen pre-fills from one
 * and the owner confirms or edits every field before anything is saved. None of
 * these become database defaults: an invented currency would quietly relabel
 * every figure (§11.1). Adding a market is one entry here.
 */
export const MARKET_PRESETS = {
  IN: {
    label: 'India',
    currencyCode: 'INR',
    timezone: 'Asia/Kolkata',
    /** Lakh and crore grouping: ₹1,23,456.00. */
    locale: 'en-IN',
    feedUom: 'kg',
  },
} as const

export type MarketCode = keyof typeof MARKET_PRESETS

export const DEFAULT_MARKET: MarketCode = 'IN'

/** Offered as suggestions. Any short unit is accepted. */
export const FEED_UOM_SUGGESTIONS = ['kg', 'bag', 'quintal', 'tonne'] as const

/** Number formats offered in the UI. The API accepts any locale the runtime supports. */
export const DISPLAY_LOCALES = [
  { code: 'en-IN', label: 'English (India)' },
  { code: 'hi-IN', label: 'हिन्दी (भारत)' },
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
] as const

/** An ISO 4217 code the runtime can format, in canonical upper case. */
export function isCurrencyCode(value: string): boolean {
  return /^[A-Z]{3}$/.test(value) && Intl.supportedValuesOf('currency').includes(value)
}

/**
 * An IANA timezone the runtime knows, aliases included.
 *
 * Not checked against `Intl.supportedValuesOf('timeZone')`: that list holds only
 * canonical names, and some engines list India as `Asia/Calcutta`, so a correct
 * `Asia/Kolkata` would fail.
 */
export function isTimeZone(value: string): boolean {
  if (value.length === 0) return false
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: value }).resolvedOptions().timeZone.length > 0
  } catch {
    return false
  }
}

/** A locale tag the runtime has number-formatting data for. */
export function isDisplayLocale(value: string): boolean {
  try {
    return Intl.NumberFormat.supportedLocalesOf([value]).length === 1
  } catch {
    return false
  }
}

/**
 * The calendar date at `at` in `timeZone`, as `YYYY-MM-DD`.
 *
 * India runs 5½ hours ahead of UTC, so between midnight and 05:30 IST the UTC
 * date is still yesterday. Anything that defaults to "today" must ask the farm's
 * timezone, not the server's clock.
 */
export function calendarDateIn(timeZone: string, at: Date): IsoDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(at)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')}`
}

export interface FarmSettingsRecord {
  farmId: string
  name: string
  /** ISO 4217. Every amount the farm records is in this currency. */
  currencyCode: string
  /** IANA name. Decides which calendar day "today" is. */
  timezone: string
  /** Number formatting only, e.g. `en-IN` for 1,23,456.00. */
  locale: string
  /** Default unit for new feed materials. Each material keeps its own unit. */
  feedUom: string
  layerStartWeek: number
  expectedLayingWeeks: number
  /** Placeholder 300 until a breed curve is loaded (§11.2). */
  expectedEggsPerHen: number
  /** What one spent hen fetches at the end of lay. Null until set (§11.4). */
  expectedSpentHenSalvagePerBird: NumericString | null
  /**
   * True once any money has been recorded. Amounts are stored as bare numbers
   * in the farm's currency, so changing the code afterwards would relabel them.
   */
  currencyLocked: boolean
}

/** What the setup screen sends. Estimates left out fall back to the schema defaults. */
export interface NewFarmInput {
  name: string
  currencyCode: string
  timezone: string
  locale: string
  feedUom: string
  layerStartWeek?: number
  expectedLayingWeeks?: number
  expectedEggsPerHen?: number
  expectedSpentHenSalvagePerBird?: NumericString | null
}

export type FarmSettingsPatch = Partial<Omit<FarmSettingsRecord, 'farmId' | 'currencyLocked'>>
